import { useContext, useEffect } from 'react';
import { Anchor, Breadcrumbs, Container, Divider, Group, LoadingOverlay, Tabs } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { IconBulb, IconCheckbox, IconPilcrow, IconSettings } from '@tabler/icons';
import { ConstantS } from '../../../Service/ConstantS';
import { MixpanelS } from '../../../Service/MixpanelS';
import { ContractTemplateContext, useAgreements } from '../../../Service/Context/ContractTemplateContext';
import DocumentAgreements from '../../Document/DocumentAgreements';
import { RoutingS } from '../../../Service/RoutingS';
import BookitupAlert from '../../../Atoms/BookitupAlert';
import LinkText from '../../../Atoms/LinkText';
import DocumentLanguagePicker from '../../../Atoms/inputs/DocumentLanguagePicker';
import ContractTemplateHeadActions from './ContractTemplateHeadActions';
import ContractTemplateLayoutSection from './sections/ContractLayoutSection';
import ContractTemplateSignatureSection from './sections/ContractSignatureSection';
import ContractTemplateContentSection from './sections/ContractContentSection';
import ContractPdfSection from './sections/ContractPdfSection';

const ContractTemplate = () => {
  const navigate = useNavigate();
  const { loading, template, savePatch } = useContext(ContractTemplateContext);
  const { ...agreementsProps } = useAgreements();
  const { name, language } = template ?? {};

  useEffect(() => {
    MixpanelS.track(ConstantS.TrackingEvents.ContractTemplateOpened);
  }, []);

  return (
    <Container p={0} pos="relative">
      <Group position="apart" sx={{ alignItems: 'center' }}>
        <Breadcrumbs>
          <Anchor onClick={() => navigate('/settings/contracts')}>Vertrags-Vorlagen</Anchor>
          <Anchor sx={{ pointerEvents: 'none' }}>
            <b>{loading ? 'Laden ...' : name || 'Nicht gefunden '}</b>
          </Anchor>
        </Breadcrumbs>
        <ContractTemplateHeadActions />
      </Group>
      <Divider my="xs" />
      <BookitupAlert mb="xs" title="Tipp" icon={<IconBulb />}>
        Gib deinen Vertrag über das{' '}
        <LinkText
          span
          onClick={() =>
            RoutingS.openInNewTab('https://app.bookitup.de/academy/channels/offer-service?episodeId=ye6osph0pm')
          }
        >
          Online-Portal
        </LinkText>
        {'  '}
        frei. Damit können deine Kunden den Vertrag bequem digital unterzeichnen.
      </BookitupAlert>
      <Tabs
        styles={{
          tab: {
            '&[data-active]': {
              fontWeight: 'bolder',
            },
          },
        }}
        defaultValue="content"
      >
        <Tabs.List>
          <Tabs.Tab value="content" icon={<IconPilcrow size={14} />}>
            Inhalt
          </Tabs.Tab>
          <Tabs.Tab value="settings" icon={<IconSettings size={14} />}>
            Einstellungen
          </Tabs.Tab>
          <Tabs.Tab value="agreements" icon={<IconCheckbox size={14} />}>
            Vereinbarungen
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="content" pt="xl">
          <ContractTemplateContentSection />
        </Tabs.Panel>
        <Tabs.Panel
          value="settings"
          pt="xs"
          sx={(theme) => ({ color: theme.colorScheme === 'dark' ? 'white' : 'black' })}
        >
          <Group mt="md">
            <DocumentLanguagePicker
              variant="select"
              language={language}
              onLanguageChange={template ? (newLanguage) => savePatch({ language: newLanguage }) : undefined}
            />
          </Group>
          <Divider my={30} />
          <ContractTemplateLayoutSection />
          <Divider my={30} />
          <ContractTemplateSignatureSection />
          <Divider my={30} />
          <ContractPdfSection />
        </Tabs.Panel>
        <Tabs.Panel value="agreements" pt="xs">
          {template && <DocumentAgreements documentId={template.id} {...agreementsProps} />}
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
};

export default ContractTemplate;
